/*
  TagsSection.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Makes.
import { makeStyles } from '../../../makes/makeStyles';

// Components.
import CheckboxWithLabel from '../../CheckboxWithLabel';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    '& > .noneText': {
      color: theme.color_text_disabled,
      fontStyle: 'italic',
    },
    '& > .tagsContainer': {
      display: 'flex',
      flexWrap: 'wrap',
      margin: theme.space(-0.5, 0, 0, -1.5),
      '& > label': {
        margin: theme.space(0.5, 0, 0, 1.5),
        // Below styles needed for proper overflow.
        overflow: 'hidden',
        display: 'inline-block',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  }
}));

/**
 * Left sidebar component.
 */
export default function TagsSection({
  className,
  graphData,
  tagFilter,
  disabled = false,
  onChangeTagFilter,
}) {

  // Get all tags and their counts.

  const nodes = graphData?.nodes || null;

  const [tagSet, tagOptions] = useMemo(() => {

    // Get [ tags -> counts ] map.
    let noneCount = 0;
    const tagCounts = new Map();
    if (nodes) {
      for (const node of nodes) {
        if (node._botech?.tags?.length) {
          for (const tag of node._botech.tags) {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          }
        } else {
          noneCount += 1;
        }
      }
    }

    // Get tags sorted alphabetically.
    const sortedOptions = [...tagCounts.entries()]
      .map(arr => ({ tag: arr[0], count: arr[1] }))
      .sort((a, b) => a.tag.localeCompare(b.tag));

    // Add extra "none" tag.
    const tagSet = new Set(tagCounts.keys());
    if (noneCount > 0) {
      sortedOptions.push({ tag: null, count: noneCount });
      tagSet.add(null);
    }

    // Return all tags labels, and options.
    return [tagSet, sortedOptions];

  }, [nodes]);

  // Classes.

  const classes = useStyles();
  let useClass = classes.root;
  if (className) {
    useClass += ` ${className}`;
  }

  return (
    <div className={useClass}>
      {
        tagOptions.length > 0 ? (
          <div className="tagsContainer">
            {
              tagOptions.map(({ tag, count }) => {

                const name = tag || '[none]';

                function handleChange(event) {
                  const checked = event.target.checked;
                  if (onChangeTagFilter) {
                    onChangeTagFilter(currSet => {
                      
                      // Toggle tag on/off.
                      const nextSet = currSet ? new Set(currSet) : new Set(tagSet);
                      if (checked) {
                        nextSet.add(tag);
                      } else {
                        nextSet.delete(tag);
                      }

                      // If all are checked, remove filter.
                      return nextSet.size < tagSet.size ? nextSet : null;

                    });
                  }
                }

                return (
                  <CheckboxWithLabel
                    key={name}
                    label={name}
                    title={`${name} (${count})`}
                    checked={!tagFilter || tagFilter.has(tag)}
                    onChange={handleChange}
                  />
                );
              })
            }
          </div>
        ) : (
          <span className="noneText body2">(none)</span>
        )
      }
    </div>
  );
}
